import { BadgePercent, Building2, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import CustomerHeader, { CustomerFooter } from "@/components/customer/CustomerHeader";
import { useShopper } from "@/components/customer/ShopperProvider";
import Img from "@/components/Img";
import { useEffect, useState } from "react";
import HotelDetail from "@/pages/customer/HotelDetail";
import { isCustomHost } from "@/pages/customer/PropertySite";
import { http } from "@/lib/api";
import { useT, useLang } from "@/lib/i18n";
import SearchBar from "@/components/customer/SearchBar";
import FeaturedPromos from "@/components/customer/FeaturedPromos";
import { clearBrand } from "@/components/customer/Brand";
import { useStay, stayQuery } from "@/lib/api";

const PERKS = [
  { icon: BadgePercent, t: "Harga langsung dari hotel", d: "Tarif terbaik tanpa perantara" },
  { icon: ShieldCheck, t: "Pembayaran aman", d: "QRIS, Virtual Account, e-wallet & kartu" },
  { icon: Sparkles, t: "Konfirmasi instan", d: "Kode booking langsung terkirim" },
];

export default function Home() {
  const [hostPid, setHostPid] = useState(isCustomHost() ? undefined : null);
  useEffect(() => {
    if (hostPid !== undefined) return;
    http.get("/public/resolve", { params: { host: window.location.hostname } }).then((r) => setHostPid(r.data.id)).catch(() => setHostPid(null));
  }, [hostPid]);
  if (hostPid === undefined) return null;
  if (hostPid) return <HotelDetail propertyId={hostPid} />;
  return <MainHome />;
}

function MainHome() {
  const stay = useStay();
  const { site } = useShopper();
  const t = useT();
  useEffect(() => { clearBrand(); }, []);
  const { lang } = useLang();
  const L = (k) => (lang === "en" && site?.[`${k}_en`]) || site?.[k];
  const [promos, setPromos] = useState([]);
  useEffect(() => { http.get("/public/featured-promos").then((r) => setPromos(r.data)).catch(() => setPromos([])); }, []);
  const DESTS = site?.destinations || [];
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <CustomerHeader />
      <section className="relative">
        <div className="absolute inset-0 h-[520px] overflow-hidden">
          {site && <Img src={site.hero_image} size="lg" eager sizes="100vw" alt="Hero" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917]/85 via-[#1C1917]/50 to-[#1C1917]/10" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#F7F4EF]" />
        </div>
        <div className="relative z-20 mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-6 sm:pt-28">
          <p className="fade-up mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-medium text-white backdrop-blur-md">
            <Building2 className="h-4 w-4" /> {L("tagline")}
          </p>
          <h1 className="fade-up max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            {L("hero_title")}
          </h1>
          <p className="fade-up mt-4 max-w-xl text-base text-white/85 md:text-lg" style={{ animationDelay: "160ms" }}>
            {L("hero_subtitle")}
          </p>
          <div className="fade-up mt-10" style={{ animationDelay: "240ms" }}><SearchBar initial={stay} /></div>
        </div>
      </section>

      <section className="relative z-20 mx-auto mt-6 grid max-w-7xl gap-4 px-4 sm:grid-cols-3 sm:px-6">
        {PERKS.map((pk) => { const I = pk.icon; return (
          <div key={pk.t} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
            <span className="rounded-lg bg-blue-50 p-2.5 text-[#5392F9]"><I className="h-5 w-5" /></span>
            <div><p className="font-heading font-bold text-slate-900">{t(pk.t)}</p><p className="text-sm text-slate-500">{t(pk.d)}</p></div>
          </div>
        ); })}
      </section>

      <FeaturedPromos cards={promos} />

      <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("Destinasi populer di Indonesia")}</h2>
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {DESTS.map((d, i) => (
            <Link key={d.city} to={`/search?${stayQuery({ ...stay, city: d.city })}`} data-testid={`dest-card-${d.city.toLowerCase()}`}
              className={`hover-lift group relative overflow-hidden rounded-2xl ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}>
              <Img src={d.image} size={i === 0 ? "lg" : "md"} alt={d.city} className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${i === 0 ? "h-56 lg:h-full" : "h-56"}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="font-heading text-xl font-extrabold">{d.city}</p>
                <p className="text-sm text-white/80">{(lang === "en" && d.note_en) || d.note}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <CustomerFooter />
    </div>
  );
}
